
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import FilterUserServicepoints from "@/components/user/FilterUserServicepoints.vue";
import Pagination from "@/components/common/Pagination.vue";
import IconEdit from "@/components/common/icon/IconEdit.vue";
import { IServicepointUserTable, IUpdateUser, IUpdateUserClient, IUserDetail } from "@/entities";
import { Action, Getter } from "vuex-class";
import UserServicepointsTableAction from "./UserServicepointsTableAction.vue";
import { BvTableFieldArray } from "bootstrap-vue";
import { i18n } from "@/i18n";
import { EventBus } from "@/event-bus";
import { SweetAlertResult } from "sweetalert2";

@Component({
  name: "user-servicepoints-approvals-table",
  components: {
    FilterUserServicepoints,
    UserServicepointsTableAction,
    Pagination,
    IconEdit,
  },
})
export default class UserServicepointsApprovalsTable extends Vue {
  @Prop({ required: true }) user!: IUserDetail;
  @Getter("getLoggedUser") loggedUser!: IUserDetail | undefined;
  @Action("removeUserServicepointApprovals") removeUserServicepointApprovals!: (payload: {
    idUser?: number;
    updateUser: IUpdateUser;
  }) => Promise<boolean | undefined>;

  public params: IServicepointUserTable = {};
  public checkedAll = false;
  public currentPage = 1;
  public pageSize = 10;
  public sortBy = "client";
  public sortDesc = false;
  public allDeselect = true;
  public component = "approval";

  mounted(): void {
    EventBus.$on("filter-user-servicepoints-table", () => {
      this.onChangeCurrentPage(1);
    });
    this.checkDeleteDisabled();
  }

  beforeDestroy(): void {
    EventBus.$off("filter-user-servicepoints-table");
  }

  get userApprovalsChangeDetector(): string {
    return JSON.stringify(this.user.approvals);
  }

  @Watch("userApprovalsChangeDetector")
  onUserApprovalsChange(): void {
    this.checkedAll = false;
    this.$nextTick(() => {
      this.checkDeleteDisabled();
    });
  }

  onChangeCurrentPage(currentPage: number): void {
    this.currentPage = currentPage;
    // Verificar estado del boton al cambiar página
    this.checkDeleteDisabled();
  }

  get isNotSameUserAndIsAdmin(): boolean {
    const userExists = Boolean(this.user && this.user.username);
    const isDifferentUser = userExists && this.user.username !== this.loggedUser?.username;
    const isAdmin = Boolean(this.loggedUser?.admin);
    return isDifferentUser || isAdmin;
  }

  get selectAllLength(): number {
    return this.servicePointsApprovalsFiltered.filter((servicepoint) => servicepoint.checked)
      .length;
  }

  private checkDeleteDisabled(): void {
    // Usar doble $nextTick para asegurar que todas las actualizaciones de Vue se completen
    this.$nextTick(() => {
      this.$nextTick(() => {
        const selectedCount = this.servicePointsApprovalsFiltered.filter(
          (servicepoint) => servicepoint.checked === true
        ).length;
        this.allDeselect = selectedCount === 0;
      });
    });
  }

  selectAll(): void {
    const newCheckedState = !this.checkedAll;
    this.servicePointsApprovalsFiltered.forEach((servicepoint) => {
      servicepoint.checked = newCheckedState;
    });
    this.checkedAll = newCheckedState;
    this.checkDeleteDisabled();
  }

  selectOne(): void {
    this.checkedAll = this.servicePointsApprovalsFiltered.every(
      (servicepoint) => servicepoint.checked
    );
    this.checkDeleteDisabled();
  }

  get servicepoints(): IServicepointUserTable[] {
    const servicepointsApprovals: IServicepointUserTable[] = [];

    if (!this.user.approvals) {
      return servicepointsApprovals;
    }

    this.user.approvals.forEach((clientApproval) => {
      clientApproval.subclients?.forEach((subclient) => {
        subclient.servicepoints?.forEach((servicepoint) => {
          servicepointsApprovals.push({
            id: servicepoint.id,
            clientId: clientApproval.id,
            client: `${clientApproval.codeSol} - ${clientApproval.description}`,
            centerId: subclient.id,
            center: `${subclient.codeSol} - ${subclient.description}`,
            description: `${servicepoint.codeSol} - ${servicepoint.description}`,
            checked: false,
          });
        });
      });
    });

    return servicepointsApprovals;
  }

  get servicePointsApprovalsFiltered(): IServicepointUserTable[] {
    return this.servicepoints.filter((servicepoint) => {
      return (
        (!this.params.client ||
          servicepoint.client?.toUpperCase().includes(this.params.client.toUpperCase())) &&
        (!this.params.center ||
          servicepoint.center?.toUpperCase().includes(this.params.center.toUpperCase())) &&
        (!this.params.description ||
          servicepoint.description?.toUpperCase().includes(this.params.description.toUpperCase()))
      );
    });
  }

  get fields(): BvTableFieldArray {
    return [
      {
        key: "actions-left",
        label: "",
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell-first",
      },
      {
        key: "client",
        label: i18n.t("label.client") as string,
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle align-middle",
      },
      {
        key: "center",
        label: i18n.t("label.subclient") as string,
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle align-middle",
      },
      {
        key: "description",
        label: i18n.t("label.servicepoint") as string,
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle align-middle",
      },
      {
        key: "actions-right",
        label: "",
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell-last",
      },
    ];
  }

  async removeServicepoints(): Promise<void> {
    const { isConfirmed } = await this.modalConfirm();
    if (isConfirmed) {
      let updateUser: IUpdateUser;
      let clients: IUpdateUserClient[] = [];

      this.servicePointsApprovalsFiltered.forEach((servicepoint) => {
        if (servicepoint.checked) {
          if (this.user.id && servicepoint.clientId && !servicepoint.id) {
            clients.push({
              id: servicepoint.clientId,
              subclients: [],
            });
          } else {
            clients.push({
              id: servicepoint.clientId!,
              subclients: [
                {
                  id: servicepoint.centerId,
                  servicepoints: [
                    {
                      id: servicepoint.id,
                    },
                  ],
                },
              ],
            });
          }
        }
      });
      updateUser = {
        addOnlyClient: false,
        numberOfServicesPoint: this.loggedUser?.servicesPointOnCashUser || 0,
        clients: clients,
      };
      const response = await this.removeUserServicepointApprovals({
        idUser: this.user.id,
        updateUser,
      });
      if (response) {
        this.makeToast("success", i18n.t("validation.messages.user.ok").toString());
        this.checkedAll = false;
        this.$emit("change", this.user.id);
        // Usar $nextTick para asegurar que los datos se actualicen antes de verificar estado
        this.$nextTick(() => {
          this.allDeselect = true;
        });
      } else {
        this.makeToast("warning", i18n.t("validation.messages.user.ko").toString());
      }
    }
  }

  async modalConfirm(): Promise<SweetAlertResult> {
    return this.$swal({
      title: this.$t("message.selectDelete.confirm.title", [this.selectAllLength]).toString(),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: i18n.t("label.yes").toString(),
      cancelButtonText: i18n.t("label.no").toString(),
    });
  }

  makeToast(variant: string, message: string): void {
    this.$bvToast.toast(message, {
      autoHideDelay: 4000,
      variant,
      toaster: "b-toaster-bottom-center",
      noCloseButton: true,
    });
  }
}
